import type { ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import formStyles from './Form.module.css';
import styles from './TimeWindowRows.module.css';

/**
 * Day-of-week chip labels for time-window pickers across the admin
 * surface. 0 = Sunday matches `Date.getDay()` and the schema fields
 * on Charger.guest_time_windows + Tariff.time_of_use_schedule.
 */
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Generic time-window row editor — shared component extracted in
 * Phase 7.2 of the resident-managed peer-to-peer chargers ticket
 * after this pattern accumulated three callers (Tariff TOU schedule,
 * admin Charger guest time windows, PWA My Charger guest time
 * windows).
 *
 * Schemas drifted between callers (Tariff uses `day_of_week`, Charger
 * uses `days_of_week`), so the component is generic over the row
 * shape and asks the caller to provide getter/setter accessors. That
 * keeps the persisted shapes unchanged while letting the UI
 * primitive be a single source of truth.
 *
 * The optional `extraColumn` slot supports the Tariff TOU "energy
 * rate per row" cell. Charger callers omit it and just edit
 * day-and-hour windows.
 */

export interface TimeWindowRowsExtraColumn<T> {
  /** Header label rendered once at the top of the rows. */
  label: string;
  /** Per-row renderer. The callback receives the current row and a
   *  setter that splices it back into the parent's window list. */
  render: (window: T, onRowChange: (next: T) => void, disabled: boolean) => ReactNode;
}

export interface TimeWindowRowsProps<T> {
  windows: T[];
  onChange: (next: T[]) => void;
  /** Pull the days-of-week array off a row. Caller chooses the field
   *  (`day_of_week` for Tariff vs `days_of_week` for Charger). */
  getDays: (window: T) => number[];
  setDays: (window: T, days: number[]) => T;
  getStartHour: (window: T) => number;
  setStartHour: (window: T, hour: number) => T;
  getEndHour: (window: T) => number;
  setEndHour: (window: T, hour: number) => T;
  /** Factory for a fresh row when the user taps "Add". Caller
   *  controls defaults (energy_rate, etc.). */
  createEmpty: () => T;
  extraColumn?: TimeWindowRowsExtraColumn<T>;
  /** Copy when the list is empty. Defaults to "No restriction." */
  emptyStateText?: string;
  /** Copy on the add button. Defaults to "Add window." */
  addButtonText?: string;
  /** Hour bounds. start defaults to 0, end defaults to 24 (midnight
   *  next day). Tariff TOU lets end = 24; Charger guest windows do
   *  the same. */
  startHourMin?: number;
  startHourMax?: number;
  endHourMin?: number;
  endHourMax?: number;
  /** When true, the remove button stays enabled even at one row.
   *  Tariff requires at least one row; Charger guest windows allow
   *  full deletion (empty array = no restriction). */
  allowEmpty?: boolean;
  disabled?: boolean;
}

function clampHour(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  if (n < min) return min;
  if (n > max) return max;
  return Math.floor(n);
}

export function TimeWindowRows<T>({
  windows,
  onChange,
  getDays,
  setDays,
  getStartHour,
  setStartHour,
  getEndHour,
  setEndHour,
  createEmpty,
  extraColumn,
  emptyStateText = 'No restriction.',
  addButtonText = 'Add window',
  startHourMin = 0,
  startHourMax = 23,
  endHourMin = 1,
  endHourMax = 24,
  allowEmpty = true,
  disabled = false,
}: TimeWindowRowsProps<T>) {
  const updateAt = (idx: number, mutator: (row: T) => T): void => {
    onChange(windows.map((w, i) => (i === idx ? mutator(w) : w)));
  };

  const removeAt = (idx: number): void => {
    onChange(windows.filter((_, i) => i !== idx));
  };

  const handleAdd = (): void => {
    onChange([...windows, createEmpty()]);
  };

  return (
    <div className={styles.wrap}>
      {windows.length === 0 && (
        <p className={styles.empty}>{emptyStateText}</p>
      )}

      {windows.length > 0 && (
        <div className={styles.list}>
          {windows.map((w, i) => {
            const days = getDays(w);
            const noDays = days.length === 0;
            return (
              <div key={i} className={styles.row}>
                <div className={styles.dayChips}>
                  {DAY_LABELS.map((label, dow) => {
                    const active = days.includes(dow);
                    return (
                      <button
                        type="button"
                        key={dow}
                        className={active ? styles.dayChipActive : styles.dayChip}
                        aria-pressed={active}
                        disabled={disabled}
                        onClick={() => updateAt(i, (row) => setDays(
                          row,
                          active
                            ? days.filter((y) => y !== dow)
                            : [...days, dow].sort((a, b) => a - b),
                        ))}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                <div className={styles.fieldGrid}>
                  <label className={styles.fieldGroup}>
                    <span className={styles.fieldLabel}>Start</span>
                    <input
                      className={formStyles.input}
                      type="number"
                      min={startHourMin}
                      max={startHourMax}
                      value={getStartHour(w)}
                      onChange={(e) => updateAt(i, (row) => setStartHour(
                        row,
                        clampHour(parseInt(e.target.value, 10), startHourMin, startHourMax),
                      ))}
                      disabled={disabled}
                    />
                  </label>
                  <label className={styles.fieldGroup}>
                    <span className={styles.fieldLabel}>End</span>
                    <input
                      className={formStyles.input}
                      type="number"
                      min={endHourMin}
                      max={endHourMax}
                      value={getEndHour(w)}
                      onChange={(e) => updateAt(i, (row) => setEndHour(
                        row,
                        clampHour(parseInt(e.target.value, 10), endHourMin, endHourMax),
                      ))}
                      disabled={disabled}
                    />
                  </label>
                  {extraColumn && (
                    <label className={styles.fieldGroup}>
                      <span className={styles.fieldLabel}>{extraColumn.label}</span>
                      {extraColumn.render(
                        w,
                        (next) => updateAt(i, () => next),
                        disabled,
                      )}
                    </label>
                  )}
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => removeAt(i)}
                    aria-label="Remove window"
                    disabled={disabled || (!allowEmpty && windows.length <= 1)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>

                {noDays && (
                  <p className={styles.warning}>
                    Pick at least one day — this row matches nothing
                    until then.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        className={styles.addBtn}
        onClick={handleAdd}
        disabled={disabled}
      >
        <Plus size={13} /> {addButtonText}
      </button>
    </div>
  );
}
